import type { MatchRng } from '../match/MatchRng';
import type { ActionCandidate } from './ActionCandidate';

export interface WeightedSelectionTrace {
  method: 'WEIGHTED_SOFTMAX';
  temperature: number;
  roll: number;
}

export interface WeightedSelectionResult {
  chosen: ActionCandidate;
  trace: WeightedSelectionTrace;
}

export class WeightedActionSelector {
  select(candidates: ActionCandidate[], rng: MatchRng, temperature: number): WeightedSelectionResult {
    if (!candidates.length) throw new Error('WeightedActionSelector requires at least one candidate');
    const safeTemperature = Math.max(0.01, temperature);
    const maximum = Math.max(...candidates.map((candidate) => candidate.utilityScore));
    const weights = candidates.map((candidate) => Math.exp((candidate.utilityScore - maximum) / safeTemperature));
    const total = weights.reduce((sum, weight) => sum + weight, 0);

    for (let index = 0; index < candidates.length; index++) {
      candidates[index].selectionWeight = weights[index];
      candidates[index].selectionProbability = weights[index] / total;
    }

    const roll = rng.next();
    let accumulated = 0;
    let chosen = candidates[candidates.length - 1];
    for (const candidate of candidates) {
      accumulated += candidate.selectionProbability ?? 0;
      if (roll < accumulated) {
        chosen = candidate;
        break;
      }
    }

    return {
      chosen,
      trace: { method: 'WEIGHTED_SOFTMAX', temperature: safeTemperature, roll },
    };
  }
}
